import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { GALLERY, IMAGES } from "@/data/content";
import { prefillEnquiry } from "@/lib/scroll";
import { SectionHeading } from "./PriceMenu";


export const Gallery = () => {
  const [selected, setSelected] = useState(null);

  return (
    <section id="gallery" data-testid="gallery-section" className="border-y border-[#D4AF37]/10 bg-[#F7F2E8]/60 py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading eyebrow="Our Work" title="The Gallery" testId="gallery-heading" />

        <motion.div layout className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {GALLERY.map((g, i) => (
              <motion.button
                layout
                key={g.id}
                data-testid={`gallery-item-${g.id}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
                onClick={() => setSelected(g)}
                className="group relative overflow-hidden rounded-sm"
              >
                <img
                  src={IMAGES[g.src]}
                  alt={g.title}
                  className="h-72 w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 md:h-96"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1B3A6B]/90 via-transparent to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-100" />
                <div className="absolute bottom-0 left-0 p-6 text-left">
                  <p className="font-label text-[10px] tracking-[0.3em] text-[#D4AF37]">{g.category.toUpperCase()}</p>
                  <p className="mt-1 font-display text-xl italic text-[#FAF8F5]">{g.title}</p>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-[#FDFBF7]/90 p-4 backdrop-blur-md"
            onClick={() => setSelected(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.97 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative grid max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-sm border border-[#D4AF37]/30 bg-[#FFFFFF] md:grid-cols-2"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid="gallery-lightbox-close"
                onClick={() => setSelected(null)}
                className="absolute right-4 top-4 z-10 rounded-full border border-[#D4AF37]/40 bg-[#FDFBF7]/70 p-2 text-[#8A6D1F] transition-colors hover:bg-[#D4AF37] hover:text-[#1B3A6B]"
                aria-label="Close"
              >
                <X size={18} />
              </button>
              <img src={IMAGES[selected.src]} alt={selected.title} className="h-64 w-full object-cover md:h-full" />
              <div className="flex flex-col justify-center p-8 lg:p-10">
                <p className="font-label text-[10px] tracking-[0.32em] text-[#D4AF37]">{selected.category.toUpperCase()}</p>
                <h3 className="mt-2 font-display text-2xl italic text-[#8A6D1F] sm:text-3xl">{selected.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-[#2F4A78]">{selected.detail}</p>
                <button
                  data-testid="gallery-lightbox-enquire-button"
                  onClick={() => {
                    setSelected(null);
                    prefillEnquiry({ message: `I love "${selected.title}" — I'd like something inspired by this design.` });
                  }}
                  className="mt-8 rounded-full bg-[#D4AF37] px-7 py-3 text-sm font-semibold text-[#1B3A6B] transition-colors duration-300 hover:bg-[#8A6D1F]"
                >
                  Enquire About This Design
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
